import React, { useState, useCallback, useEffect, useMemo, useContext, useRef } from 'react';
import { ProductContext } from '../contexts/ProductContext';
import { AuthContext } from '../contexts/AuthContext';
import AccountService, { AccessibleProduct } from '../services/account.service';
import ProductBrandingService, { ResolvedProductBranding } from '../services/product-branding.service';
import { LOCAL_STORAGE_KEYS } from '../utils/appConstant';

const PRIMARY_COLOUR_VAR = '--bs-primary';

interface ProductProviderProps {
  children: React.ReactNode;
}

const readStoredProductId = (): number | null => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.selectedProductId);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
};

const hexToRgbTriplet = (hex: string): string | null => {
  const match = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex);
  if (!match) return null;
  const [, r, g, b] = match;
  return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;
};

const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const { token, setModuleConfig } = useContext(AuthContext);
  const [accessibleProducts, setAccessibleProducts] = useState<AccessibleProduct[]>([]);
  const [isGlobalProductScope, setIsGlobalProductScope] = useState(false);
  const [selectedProductId, setSelectedProductIdState] = useState<number | null>(readStoredProductId);
  const [activeBranding, setActiveBranding] = useState<ResolvedProductBranding | null>(null);
  const defaultPrimaryRef = useRef<{ colour: string; rgb: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const response: any = await AccountService.getProfile();
      const products: AccessibleProduct[] = response?.data?.accessibleProducts ?? [];
      setAccessibleProducts(products);
      setIsGlobalProductScope(Boolean(response?.data?.isGlobalProductScope));
      setModuleConfig(response?.data?.moduleConfig ?? {});

      const stored = readStoredProductId();
      const storedIsValid = stored !== null && products.some((p) => p.id === stored);
      if (!storedIsValid) {
        // Global users default to "All Products" (null); product-scoped users
        // default to their first accessible product (AC-7 — always pre-selected).
        const isGlobal = Boolean(response?.data?.isGlobalProductScope);
        const fallback = isGlobal ? null : products.length > 0 ? products[0].id : null;
        setSelectedProductIdState(fallback);
        if (fallback !== null) localStorage.setItem(LOCAL_STORAGE_KEYS.selectedProductId, String(fallback));
        else localStorage.removeItem(LOCAL_STORAGE_KEYS.selectedProductId);
      }
    } catch {
      setAccessibleProducts([]);
    }
  }, [setModuleConfig]);

  useEffect(() => {
    if (token) load();
    // Re-fetch (and re-resolve moduleConfig) whenever the active product changes —
    // module visibility is per-product, so switching context must refresh it (ST-003).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProductId]);

  // US-036 ST-005: apply the resolved product branding's theme colour to the topbar
  // in real time by overriding the Bootstrap --bs-primary token; restore the page's
  // original default when no product is selected (All Products / global scope).
  useEffect(() => {
    const root = document.documentElement;
    if (!defaultPrimaryRef.current) {
      const computed = getComputedStyle(root);
      defaultPrimaryRef.current = {
        colour: computed.getPropertyValue(PRIMARY_COLOUR_VAR).trim(),
        rgb: computed.getPropertyValue(`${PRIMARY_COLOUR_VAR}-rgb`).trim(),
      };
    }

    if (!token || selectedProductId === null) {
      setActiveBranding(null);
      if (defaultPrimaryRef.current) {
        root.style.setProperty(PRIMARY_COLOUR_VAR, defaultPrimaryRef.current.colour);
        root.style.setProperty(`${PRIMARY_COLOUR_VAR}-rgb`, defaultPrimaryRef.current.rgb);
      }
      return;
    }

    let cancelled = false;
    ProductBrandingService.getForProduct(selectedProductId)
      .then((response: any) => {
        if (cancelled) return;
        const resolved: ResolvedProductBranding | undefined = response?.data;
        if (!resolved) return;
        setActiveBranding(resolved);
        root.style.setProperty(PRIMARY_COLOUR_VAR, resolved.themeColour);
        const rgb = hexToRgbTriplet(resolved.themeColour);
        if (rgb) root.style.setProperty(`${PRIMARY_COLOUR_VAR}-rgb`, rgb);
      })
      .catch(() => {
        // branding is a visual enhancement only — silently keep whatever colour is already applied
      });

    return () => {
      cancelled = true;
    };
  }, [token, selectedProductId]);

  const setSelectedProduct = useCallback((id: number | null) => {
    setSelectedProductIdState(id);
    if (id !== null) localStorage.setItem(LOCAL_STORAGE_KEYS.selectedProductId, String(id));
    else localStorage.removeItem(LOCAL_STORAGE_KEYS.selectedProductId);
  }, []);

  const value = useMemo(
    () => ({ selectedProductId, setSelectedProduct, accessibleProducts, isGlobalProductScope, activeBranding }),
    [selectedProductId, setSelectedProduct, accessibleProducts, isGlobalProductScope, activeBranding]
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

export default ProductProvider;
